import {
  NewCashDrawerTotalsDTO,
  UpdateCashDrawerTotalsDTO,
} from "@/api/dto/CashDrawerTotalsDTO";
import { cashDrawerTotalsMapper } from "@/api/mappers/CashDrawerTotalsMapper";
import { CashDrawer } from "@/api/models/CashDrawers/CashDrawer";
import { CashDrawerTotals } from "@/api/models/CashDrawers/CashDrawerTotal";
import { useNotificationStore } from "@/stores/notification";
import { ref } from "vue";
import { useClient } from "../useClient";
import type { CashDrawerDTO } from "@/autogen/model/cash-drawer-dto";

export const useCashDrawer = () => {
  const client = useClient();
  const notificationStore = useNotificationStore();
  const selectedCashDrawer = ref<CashDrawerDTO | null>(null);
  const isLoading = ref<boolean>(false);

  const setSelectedCashDrawer = (cashDrawer: CashDrawerDTO) => {
    selectedCashDrawer.value = cashDrawer;
    localStorage.setItem("selectedCashDrawer", JSON.stringify(cashDrawer));
  };

  const init = () => {
    const storedCashDrawer = localStorage.getItem("selectedCashDrawer");
    if (storedCashDrawer) {
      selectedCashDrawer.value = JSON.parse(storedCashDrawer);
    } else {
      fetchAndSetDefaultCashDrawer();
    }
  };

  const fetchAllCashDrawers = async (): Promise<
    CashDrawerDTO[] | undefined
  > => {
    isLoading.value = true;
    try {
      const { usePaymentApi } = await import("@/autogen/api/payment-api");
      const paymentApi = usePaymentApi();
      const result = (await paymentApi.staffCashDrawersGet()).data;
      return result;
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar("Failed to fetch cash drawers", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAllCashDrawerTotals = async (): Promise<
    CashDrawerTotals[] | undefined
  > => {
    isLoading.value = true;
    try {
      const { data } = await client.get("/staff/cashDrawerTotals");
      return data.map(cashDrawerTotalsMapper);
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar(
        "Failed to fetch cash drawer totals",
        "error",
      );
    } finally {
      isLoading.value = false;
    }
  };

  const fetchCashDrawerTotalById = async (
    id: number,
  ): Promise<CashDrawerTotals | undefined> => {
    isLoading.value = true;
    try {
      const { data } = await client.get(`/staff/cashDrawerTotals/${id}`);
      return cashDrawerTotalsMapper(data);
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar(
        "Failed to fetch cash drawer total",
        "error",
      );
    } finally {
      isLoading.value = false;
    }
  };

  const createCashDrawerTotal = async (
    dto: NewCashDrawerTotalsDTO,
  ): Promise<CashDrawerTotals | undefined> => {
    isLoading.value = true;
    try {
      const { data } = await client.post("/staff/cashDrawerTotals", dto);
      return cashDrawerTotalsMapper(data);
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar(
        "Failed to create cash drawer total",
        "error",
      );
    } finally {
      isLoading.value = false;
    }
  };

  const updateCashDrawerTotal = async (
    id: number,
    dto: UpdateCashDrawerTotalsDTO,
  ): Promise<CashDrawerTotals | undefined> => {
    isLoading.value = true;
    try {
      const { data } = await client.put(`/staff/cashDrawerTotals/${id}`, dto);
      return cashDrawerTotalsMapper(data);
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar(
        "Failed to update cash drawer total",
        "error",
      );
    } finally {
      isLoading.value = false;
    }
  };

  const popCashDrawer = async (id: string): Promise<boolean> => {
    isLoading.value = true;
    try {
      const { data } = await client.post(`/staff/cashDrawers/${id}/pop`);
      return data.success;
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar("Failed to pop cash drawer", "error");
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const testPrinterConnection = async (id: string): Promise<boolean> => {
    isLoading.value = true;
    try {
      const { data } = await client.get(
        `/staff/cashDrawers/${id}/testConnection`,
      );
      return data.isAlive;
    } catch (error) {
      console.error(error);
      notificationStore.addSnackbar(
        "Failed to test printer connection",
        "error",
      );
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAndSetDefaultCashDrawer = async (): Promise<
    CashDrawer | undefined
  > => {
    isLoading.value = true;
    try {
      const cashDrawers = await fetchAllCashDrawers();
      if (cashDrawers && cashDrawers.length > 0) {
        setSelectedCashDrawer(cashDrawers[0]);
        return cashDrawers[0];
      } else {
        notificationStore.addSnackbar(
          "No cash drawers available. Please create one.",
          "error",
          //2minutes in milliseconds
          120000,
        );
      }
    } finally {
      isLoading.value = false;
    }
  };

  return {
    fetchAllCashDrawers,
    fetchAllCashDrawerTotals,
    fetchCashDrawerTotalById,
    createCashDrawerTotal,
    updateCashDrawerTotal,
    popCashDrawer,
    testPrinterConnection,
    selectedCashDrawer,
    setSelectedCashDrawer,
    fetchAndSetDefaultCashDrawer,
    init,
    isLoading,
  };
};
